import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import {
  FORM_UPLOAD_ALLOWED_EXTENSIONS,
  FORM_UPLOAD_ALLOWED_MIME_TYPES,
  FORM_UPLOAD_MAX_FILE_SIZE_BYTES,
} from "./constants";
import { ensureFirebaseAnonymousAuth, getFirebaseStorageClient } from "./firebaseClient";

export type UploadStatus = "queued" | "uploading" | "uploaded" | "failed" | "removed";

export interface UploadedFileRecord {
  id: string;
  fieldKey: string;
  fileName: string;
  mimeType: string;
  size: number;
  status: UploadStatus;
  progress: number;
  downloadUrl?: string;
  storagePath?: string;
  error?: string;
}

function getFileExtension(fileName: string): string {
  const idx = fileName.lastIndexOf(".");
  return idx >= 0 ? fileName.substring(idx).toLowerCase() : "";
}

export function makeUploadId(file: File): string {
  return `${file.name}-${file.size}-${file.lastModified}`;
}

export function validateUploadFile(file: File): string | null {
  const extension = getFileExtension(file.name);
  const extensionAllowed = FORM_UPLOAD_ALLOWED_EXTENSIONS.includes(extension as (typeof FORM_UPLOAD_ALLOWED_EXTENSIONS)[number]);
  const mimeAllowed = FORM_UPLOAD_ALLOWED_MIME_TYPES.includes(file.type as (typeof FORM_UPLOAD_ALLOWED_MIME_TYPES)[number]);

  if (!extensionAllowed || !mimeAllowed) {
    return "Unsupported file type. Allowed formats: PDF, DOC, DOCX, XLS, XLSX.";
  }
  if (file.size > FORM_UPLOAD_MAX_FILE_SIZE_BYTES) {
    return `File is too large. Maximum file size is ${Math.round(FORM_UPLOAD_MAX_FILE_SIZE_BYTES / (1024 * 1024))}MB.`;
  }
  return null;
}

export async function uploadFileToFirebase(
  formId: string,
  fieldKey: string,
  file: File,
  onProgress: (progress: number) => void,
): Promise<{ url: string; storagePath: string }> {
  await ensureFirebaseAnonymousAuth();
  const storage = getFirebaseStorageClient();
  const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const storagePath = `form-uploads/${formId}/${fieldKey}/${Date.now()}-${safeFileName}`;
  const storageRef = ref(storage, storagePath);
  const task = uploadBytesResumable(storageRef, file, { contentType: file.type });

  await new Promise<void>((resolve, reject) => {
    task.on(
      "state_changed",
      snapshot => {
        const pct = snapshot.totalBytes > 0 ? (snapshot.bytesTransferred / snapshot.totalBytes) * 100 : 0;
        onProgress(Math.min(100, Math.max(0, pct)));
      },
      reject,
      resolve,
    );
  });

  const url = await getDownloadURL(task.snapshot.ref);
  return { url, storagePath };
}
